import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Terminal, Plus, Pencil, Trash2, Monitor } from "lucide-react";
import { Dialog } from "@/components/ui/Dialog";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { ApiFieldErrors } from "@/components/ui/ApiFieldErrors";
import { apiFieldDescribedBy, apiFieldHasError } from "@/components/ui/api-field-errors";
import {
  useAccessKeys,
  useRenameAccessKey,
  useDeleteAccessKey,
  useDeleteSessions,
} from "../hooks/useAccessKeys";
import { CreateAccessKeyDialog } from "./CreateAccessKeyDialog";
import type { AccessKey } from "../api/access-keys.api";
import { formatDate } from "@/lib/formatters";

const CLI_ORIGINS = ["cli_browser", "cli_device"];

export function AccessKeysSection() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useAccessKeys();
  const renameKey = useRenameAccessKey();
  const deleteKey = useDeleteAccessKey();
  const deleteSessions = useDeleteSessions();
  const renameFieldErrorPrefix = useId();

  const [createOpen, setCreateOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<AccessKey | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AccessKey | null>(null);
  const [newName, setNewName] = useState("");
  const renameInvalid = apiFieldHasError(renameKey.error, "friendlyName");

  const keys = data?.accessKeys ?? [];
  const cliKeys = keys.filter((k) => !k.isSession);
  const sessions = keys.filter(
    (k) => k.isSession && !CLI_ORIGINS.includes(k.credentialOrigin ?? ""),
  );

  const mutationError = deleteKey.error ?? deleteSessions.error;

  const closeRename = () => {
    if (renameKey.isPending) return;
    setRenameTarget(null);
    renameKey.reset();
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    if (deleteTarget.isSession && deleteTarget.createdBy) {
      deleteSessions.mutate(deleteTarget.createdBy);
    } else if (deleteTarget.friendlyName) {
      deleteKey.mutate(deleteTarget.friendlyName);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-surface p-6">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-base font-semibold text-heading">
          <Terminal size={18} />
          {t("account.accessKeys")}
        </h3>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3.5 py-2 text-xs font-medium text-muted transition-colors hover:border-border-light hover:text-heading"
        >
          <Plus size={12} />
          {t("account.createAccessKey")}
        </button>
      </div>
      <p className="mt-1 text-xs text-muted">{t("account.accessKeysSubtitle")}</p>

      <ApiErrorBanner error={error} className="mt-4" />
      <ApiFieldErrors error={mutationError} className="mt-3" />

      {!isLoading && !error && cliKeys.length === 0 && sessions.length === 0 && (
        <p className="mt-4 text-sm text-muted">{t("account.noAccessKeys")}</p>
      )}

      {cliKeys.length > 0 && (
        <ul className="mt-4 divide-y divide-border">
          {cliKeys.map((key) => (
            <li key={key.friendlyName} className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm font-medium text-heading">{key.friendlyName}</p>
                <p className="mt-0.5 text-xs text-muted">
                  {t("account.accessKeyCreated")}: {formatDate(key.createdTime)} ·{" "}
                  {t("account.accessKeyExpires")}: {formatDate(key.expires)}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => {
                    setRenameTarget(key);
                    setNewName(key.friendlyName ?? "");
                  }}
                  title={t("account.renameAccessKey")}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-muted transition-colors hover:bg-elevated hover:text-heading"
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(key)}
                  title={t("account.revokeAccessKey")}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-muted transition-colors hover:bg-danger/10 hover:text-danger"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {sessions.length > 0 && (
        <>
          <h4 className="mt-5 flex items-center gap-1.5 text-xs font-medium text-muted">
            <Monitor size={12} />
            {t("account.activeSessions")}
          </h4>
          <ul className="mt-2 divide-y divide-border">
            {sessions.map((session, index) => (
              <li
                key={session.friendlyName ?? session.createdBy ?? index}
                className="flex items-center justify-between py-3"
              >
                <div>
                  <p className="text-sm text-heading">{session.createdBy}</p>
                  <p className="mt-0.5 text-xs text-muted">
                    {t("account.accessKeyCreated")}: {formatDate(session.createdTime)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(session)}
                  title={t("account.revokeSession")}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-muted transition-colors hover:bg-danger/10 hover:text-danger"
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <CreateAccessKeyDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      <Dialog
        open={renameTarget !== null}
        onClose={closeRename}
        title={t("account.renameAccessKey")}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (renameKey.isPending) return;
            const trimmed = newName.trim();
            if (!renameTarget?.friendlyName || !trimmed || trimmed === renameTarget.friendlyName)
              return;
            renameKey.mutate(
              { friendlyName: renameTarget.friendlyName, newFriendlyName: trimmed },
              { onSuccess: () => setRenameTarget(null) },
            );
          }}
        >
          <input
            id="access-key-rename"
            type="text"
            value={newName}
            onChange={(e) => {
              setNewName(e.target.value);
              if (renameKey.error) renameKey.reset();
            }}
            aria-label={t("account.accessKeyNameLabel")}
            data-autofocus
            readOnly={renameKey.isPending}
            aria-disabled={renameKey.isPending || undefined}
            aria-invalid={renameInvalid ? true : undefined}
            aria-describedby={apiFieldDescribedBy(
              renameKey.error,
              "friendlyName",
              renameFieldErrorPrefix,
            )}
            className="h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-heading transition-colors focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo aria-disabled:cursor-not-allowed aria-disabled:opacity-50 hover:border-border-light"
          />
          <ApiFieldErrors
            error={renameKey.error}
            fieldLabels={{ friendlyName: t("account.accessKeyNameLabel") }}
            idPrefix={renameFieldErrorPrefix}
            className="mt-3"
          />
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={closeRename}
              aria-disabled={renameKey.isPending || undefined}
              className="rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={
                !renameKey.isPending &&
                (!newName.trim() ||
                  !renameTarget?.friendlyName ||
                  newName.trim() === renameTarget.friendlyName)
              }
              aria-disabled={renameKey.isPending || undefined}
              aria-busy={renameKey.isPending || undefined}
              className="rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
            >
              {renameKey.isPending ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </form>
      </Dialog>

      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={deleteTarget?.isSession ? t("account.revokeSession") : t("account.revokeAccessKey")}
        description={
          deleteTarget?.isSession
            ? t("account.revokeSessionConfirm", { createdBy: deleteTarget?.createdBy ?? "" })
            : t("account.revokeAccessKeyConfirm", { name: deleteTarget?.friendlyName ?? "" })
        }
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        destructive
        isPending={deleteKey.isPending || deleteSessions.isPending}
      />
    </div>
  );
}
